// components/BookingRefLine.js
//
// The booking's identity line under a guest name on the readiness cards:
// confirmation code · source platform.
//
// Shared by ArrivalReadinessCard and DepartureReadinessCard because the two
// stack on the dashboard and must stay identical siblings — the same reason
// readinessCards.css is shared. It also keeps the code + platform pair on the
// mobile blocks, where previously neither was shown at all.
//
// Platform name comes from platformUtils (never a local map — CLAUDE.md), so
// "Gathern"/"Booking.com"/"حجز مباشر" stay consistent with the bookings list.

import React from 'react';
import { Link } from 'react-router-dom';
import { getPlatformDisplayName } from '../utils/platformUtils';

/**
 * @param {string}  confirmationCode
 * @param {string}  [platform]  omitted when falsy — never pass the literal 'unknown'
 * @param {string}  [bookingId] when given, the CODE becomes a link to that booking.
 *
 * `bookingId` is optional and purely additive (2026-08-01, for the unified inbox): the two
 * readiness cards pass nothing and render exactly as before. Only the code is wrapped — the
 * platform stays plain text, because the code is the thing an operator wants to open and
 * linkifying the whole line would make the separator and the platform name clickable too.
 *
 * NOT safe inside a <button>. An <a> nested in a button is invalid HTML and its click fights
 * the button's own handler; a caller whose row is already clickable should omit `bookingId`.
 */
const BookingRefLine = ({ confirmationCode, platform, bookingId }) => (
  // No `direction: ltr` here — that pins the whole line to the LEFT of an RTL
  // cell, away from the guest name it belongs to. The line stays RTL and only
  // the code is bidi-isolated (<bdi>), so "HM-C162622D" keeps its own glyph
  // order without dragging the line's alignment with it.
  <small className="text-muted d-block">
    {bookingId && confirmationCode
      ? <Link to={`/bookings/${bookingId}`}><bdi>{confirmationCode}</bdi></Link>
      : <bdi>{confirmationCode || '—'}</bdi>}
    {platform && (
      // Muted and lighter than the code: the code is what an operator searches
      // by, the platform is only context for where to go if they must act.
      <span style={{ color: '#adb5bd' }}> · <bdi>{getPlatformDisplayName(platform)}</bdi></span>
    )}
  </small>
);

export default BookingRefLine;
